import { FormProvider, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaLoader, PaziaMultiSelect, PaziaSwitch } from "components/atoms";
import { useProductCardSettings, shopApi } from "api/shop";
import { CourseGroupAttributeType, useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import { useToaster } from "utils";
import { useState, useEffect } from "react";

const CARD_DISPLAY_ATTRIBUTE_TYPES = [
  CourseGroupAttributeType.Boolean,
  CourseGroupAttributeType.DictionarySingleSelect,
  CourseGroupAttributeType.DictionaryMultiSelect,
];

interface FormValues {
  badgeAttributeIds: string[];
  listAttributeIds: string[];
  showImage: boolean;
  showCourseGroupName: boolean;
}

export const ShopProductCardForm = () => {
  const { productCardSettings, isLoading, invalidate } = useProductCardSettings();
  const { allAttributes } = useCourseGroupAttributesAll();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const cardDisplayAttributes = allAttributes.filter((a) =>
    CARD_DISPLAY_ATTRIBUTE_TYPES.includes(a.attributeType),
  );
  const cardDisplayAttributeOptions = cardDisplayAttributes.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  const methods = useForm<FormValues>({
    defaultValues: {
      badgeAttributeIds: productCardSettings?.badgeAttributeIds?.map(String) ?? [],
      listAttributeIds: productCardSettings?.listAttributeIds?.map(String) ?? [],
      showImage: productCardSettings?.showImage ?? true,
      showCourseGroupName: productCardSettings?.showCourseGroupName ?? true,
    },
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (productCardSettings) {
      reset({
        badgeAttributeIds: productCardSettings.badgeAttributeIds?.map(String) ?? [],
        listAttributeIds: productCardSettings.listAttributeIds?.map(String) ?? [],
        showImage: productCardSettings.showImage ?? true,
        showCourseGroupName: productCardSettings.showCourseGroupName ?? true,
      });
    }
  }, [productCardSettings, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      await shopApi.updateProductCardSettings({
        badgeAttributeIds: data.badgeAttributeIds.map(Number),
        listAttributeIds: data.listAttributeIds.map(Number),
        showImage: data.showImage,
        showCourseGroupName: data.showCourseGroupName,
      });
      showSuccess("Zapisano ustawienia karty produktu");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać ustawień karty produktu");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-4xl gap-6">
        <div>
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Karta produktu</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Wybierz atrybuty wyświetlane na stronie szczegółowej produktu (kursu)
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <PaziaSwitch name="showImage" label="Wyświetlaj zdjęcie" />
          <PaziaSwitch name="showCourseGroupName" label="Wyświetlaj nazwę grupy kursów" />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <PaziaMultiSelect
            name="badgeAttributeIds"
            label="Atrybuty dla badge'ów"
            options={cardDisplayAttributeOptions}
          />
          <PaziaMultiSelect
            name="listAttributeIds"
            label="Atrybuty dla listy"
            options={cardDisplayAttributeOptions}
          />
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Wybierz atrybuty (słownikowe lub logiczne), których wartości będą wyświetlane jako badge'e i elementy listy na
          stronie produktu
        </p>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};
